/**
 * Athos API request handler
 * Manages Athos API requests with session tracking and request header generation
 */

import { AthosParams } from 'libs/athos/classes/athos-params'
import { getApiOrigin } from 'libs/athos/athos-utils'

const REQUEST_TIMEOUT_MS = 8000
const RETRYABLE_STATUS = new Set<number>([408, 425, 429, 500, 502, 503, 504])

export class AthosFetcher {
  params: AthosParams

  constructor(category?: string) {
    this.params = new AthosParams(category)
  }

  /**
   * Identity is read from the `userId`/`sessionId`/`pageLoadId` query params
   * (set by `updateParams()`), never from headers — the API doesn't ingest
   * `athos-*` headers, and sending them only forces a CORS preflight on every
   * request.
   */
  generateHeaders(): Headers {
    return new Headers({
      Accept: 'application/json'
    })
  }

  generateRequestInit(): RequestInit {
    return {
      method: 'GET',
      headers: this.generateHeaders()
    }
  }

  generateOrigin(): string {
    return getApiOrigin(this.params.siteID)
  }

  updateParams(): void {
    this.params.setSiteIDParam()

    this.params.set('beacon', 'true')

    this.params.setUserID()
    this.params.setSessionID()
    this.params.setPageLoadID()
  }

  generateURL(endpoint = ''): URL {
    const origin = this.generateOrigin()
    const params = this.params.toString()
    return new URL(`${origin}${endpoint}?${params}`)
  }

  async fetch(controller?: AbortController): Promise<any> {
    // A blank `athos_*_site_id` setting renders through Liquid's `nil | json`
    // as literal `null`, which would otherwise produce a request to
    // `https://null.a.athoscommerce.net/...`.
    if (!this.params.siteID) return {}

    const url = this.generateURL()
    return this.request(url, controller?.signal, true)
  }

  /**
   * Perform a single request attempt with a timeout guard, retrying once on
   * transient failures. The caller's `externalSignal` (used to cancel a request
   * that a newer one supersedes) is respected without being mutated: each
   * attempt uses its own controller linked to the external signal, so a retry
   * after a timeout doesn't reuse an already-aborted caller controller.
   */
  async request(url: URL, externalSignal: AbortSignal | undefined, allowRetry: boolean): Promise<any> {
    const attemptController = new AbortController()
    const requestInit = this.generateRequestInit()
    requestInit.signal = attemptController.signal

    const onExternalAbort = () => attemptController.abort()
    if (externalSignal) {
      if (externalSignal.aborted) attemptController.abort()
      else externalSignal.addEventListener('abort', onExternalAbort, { once: true })
    }

    let timedOut = false
    const timeout = setTimeout(() => {
      timedOut = true
      attemptController.abort()
    }, REQUEST_TIMEOUT_MS)

    try {
      const response = await fetch(url, requestInit)

      if (!response.ok) {
        if (allowRetry && RETRYABLE_STATUS.has(response.status)) {
          return this.request(url, externalSignal, false)
        }
        throw new Error(`Athos request failed: ${response.status} ${response.statusText}`)
      }

      return await response.json()
    } catch (error) {
      if (externalSignal?.aborted) throw error

      if (allowRetry) return this.request(url, externalSignal, false)

      if (timedOut) {
        throw new Error(`Athos request timed out after ${REQUEST_TIMEOUT_MS}ms`)
      }
      throw error
    } finally {
      clearTimeout(timeout)
      if (externalSignal) externalSignal.removeEventListener('abort', onExternalAbort)
    }
  }
}
